// Stream Health Monitor Service
// Monitors stream performance and handles recovery

interface StreamMetrics {
  streamId: number;
  bufferingEvents: number;
  connectionDrops: number;
  lastBufferingTime: number;
  totalWatchTime: number;
  qualitySwitches: number;
  errors: string[];
  lastHealthCheck: number;
  bitrate?: number;
  resolution?: string;
  framerate?: number;
  bufferHealth?: number;
  errorCount?: number;
}

class StreamHealthMonitor {
  private metrics = new Map<number, StreamMetrics>();
  private readonly HEALTH_CHECK_INTERVAL = 30000; // 30 seconds
  private readonly MAX_BUFFERING_EVENTS = 10;
  private readonly RECOVERY_ATTEMPTS = 3;

  constructor() {
    // Periodic health checks
    setInterval(() => {
      this.performHealthChecks();
    }, this.HEALTH_CHECK_INTERVAL);
  }

  updateMetrics(streamId: number, updates: Partial<StreamMetrics>) {
    const current = this.metrics.get(streamId) || {
      streamId,
      bufferingEvents: 0,
      connectionDrops: 0,
      lastBufferingTime: 0,
      totalWatchTime: 0,
      qualitySwitches: 0,
      errors: [],
      lastHealthCheck: Date.now()
    };

    this.metrics.set(streamId, { ...current, ...updates });
  }

  recordBuffering(streamId: number) {
    const metrics = this.metrics.get(streamId);
    if (metrics) {
      metrics.bufferingEvents++;
      metrics.lastBufferingTime = Date.now();

      // If too many buffering events, trigger recovery
      if (metrics.bufferingEvents >= this.MAX_BUFFERING_EVENTS) {
        console.warn(`Stream ${streamId} has excessive buffering, triggering recovery`);
        this.attemptRecovery(streamId);
      }
    }
  }

  recordConnectionDrop(streamId: number) {
    const metrics = this.metrics.get(streamId);
    if (metrics) {
      metrics.connectionDrops++;
      console.warn(`Stream ${streamId} connection dropped (${metrics.connectionDrops} total)`);
    }
  }

  recordError(streamId: number, error: string | { type: string; message: string }) {
    const metrics = this.metrics.get(streamId);
    if (metrics) {
      const errorMessage = typeof error === 'string' ? error : `${error.type}: ${error.message}`;
      metrics.errors.push(`${new Date().toISOString()}: ${errorMessage}`);
      // Keep only last 10 errors
      if (metrics.errors.length > 10) {
        metrics.errors = metrics.errors.slice(-10);
      }
      console.error(`Stream ${streamId} error: ${errorMessage}`);
    }
  }

  async attemptRecovery(streamId: number): Promise<boolean> {
    console.log(`Attempting recovery for stream ${streamId}`);

    const metrics = this.metrics.get(streamId);
    if (!metrics) return false;

    // Simple recovery: reset buffering count and emit recovery event
    metrics.bufferingEvents = 0;

    // In a real implementation, this would:
    // 1. Try different CDN endpoints
    // 2. Switch to lower quality
    // 3. Reconnect WebSocket
    // 4. Notify backend of issues

    // For now, just log and return success
    console.log(`Recovery attempted for stream ${streamId}`);
    return true;
  }

  getMetrics(streamId: number): StreamMetrics | undefined {
    return this.metrics.get(streamId);
  }

  getAllMetrics(): StreamMetrics[] {
    return Array.from(this.metrics.values());
  }

  private performHealthChecks() {
    const now = Date.now();

    for (const [streamId, metrics] of this.metrics) {
      // Check if stream is still active (has recent activity)
      const timeSinceLastActivity = now - Math.max(
        metrics.lastBufferingTime,
        metrics.lastHealthCheck
      );

      // If no activity for 5 minutes, clean up
      if (timeSinceLastActivity > 5 * 60 * 1000) {
        console.log(`Cleaning up metrics for inactive stream ${streamId}`);
        this.metrics.delete(streamId);
      } else {
        metrics.lastHealthCheck = now;
      }
    }
  }

  // Get health status for a stream
  getHealthStatus(streamId: number): 'healthy' | 'warning' | 'critical' {
    const metrics = this.metrics.get(streamId);
    if (!metrics) return 'healthy';

    if (metrics.errors.length > 5 || metrics.connectionDrops > 3) {
      return 'critical';
    }

    if (metrics.bufferingEvents > 5 || metrics.errors.length > 2) {
      return 'warning';
    }

    return 'healthy';
  }
}

// Export singleton instance
export const streamHealthMonitor = new StreamHealthMonitor();